import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import { CodeBlock } from '@atlaskit/code';
import { codeSetUpThemes, codeSetUpThemesShorthand, codeHtmlTheme, codeUseTokens, codeBabelPlugin, codeBabelPluginUsage, codeBabelPluginCompiled, codeFallbackRemovalCodemod, codeLintRules } from '../../../../snippets/code/use-tokens-in-code';
export const _frontmatter = {
  "title": "Use tokens in code",
  "description": "Learn how to set up and use design tokens in code."
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`This page explains how to set up and use
`}<a parentName="p" {...{
        "href": "https://atlassian.design/tokens/design-tokens"
      }}>{`design tokens and themes`}</a>{` in code.`}</p>
    <h2 {...{
      "id": "before-you-begin"
    }}>{`Before you begin`}</h2>
    <p>{`Make sure you’re
`}<a parentName="p" {...{
        "href": "https://atlassian.design/get-started/develop"
      }}>{`set up to use the Atlassian Design System`}</a>{`. In
particular, make sure `}<inlineCode parentName="p">{`@atlaskit/tokens`}</inlineCode>{` is installed as a dependency and use our
`}<a parentName="p" {...{
        "href": "https://atlassian.design/components/eslint-plugin-design-system/usage"
      }}>{`linting rules`}</a>{` to stay up to
date.`}</p>
    <p>{`Installing `}<inlineCode parentName="p">{`@atlaskit/tokens`}</inlineCode>{` alone does not mount the theme CSS required by design tokens—make sure
to follow the setup below.`}</p>
    <h2 {...{
      "id": "set-up-themes-and-theme-switching"
    }}>{`Set up themes and theme switching`}</h2>
    <p>{`For themes to work properly, theming must be initialized by the Atlassian application and by any
Marketplace app that extends its experience.`}</p>
    <p>{`Your application must initialize ADS theming. For client-rendered applications that are not already
using `}<inlineCode parentName="p">{`AppProvider`}</inlineCode>{` or another supported theme initializer, call
`}<a parentName="p" {...{
        "href": "https://atlassian.design/components/tokens/code#setglobalthemethemestate"
      }}><inlineCode parentName="a">{`setGlobalTheme`}</inlineCode></a>{` once at
startup. The example below uses the current default configuration.`}</p>
    <CodeBlock language="jsx" text={codeSetUpThemes} shouldWrapLongLines mdxType="CodeBlock" />
    <br></br>
    <p>{`These values are the current defaults, so this shorthand is equivalent:`}</p>
    <CodeBlock language="jsx" text={codeSetUpThemesShorthand} shouldWrapLongLines mdxType="CodeBlock" />
    <br></br>
    <p>{`Make sure theming is set up by inspecting your app HTML. You should see something like this:`}</p>
    <CodeBlock language="html" text={codeHtmlTheme} shouldWrapLongLines mdxType="CodeBlock" />
    <br></br>
    <p>{`Pay special attention to the `}<inlineCode parentName="p">{`data-color-mode="dark"`}</inlineCode>{` attribute, and also note the
`}<inlineCode parentName="p">{`data-theme="dark:dark light:light ..."`}</inlineCode>{` attribute. These reflect the current theme state. They also
match CSS selectors within the theme files so the appropriate CSS variables can be activated in
response to theme changes.`}</p>
    <h3 {...{
      "id": "set-up-atlassian-fonts"
    }}>{`Set up Atlassian fonts`}</h3>
    <p>{`To bring `}<a parentName="p" {...{
        "href": "https://atlassian.design/foundations/typography"
      }}>{`Atlassian Sans and Atlassian Mono`}</a>{` web
fonts into your app, follow our
`}<a parentName="p" {...{
        "href": "https://go.atlassian.com/set-up-fonts"
      }}>{`setup guide (Atlassians only)`}</a>{`.`}</p>
    <p>{`The typography theme is enabled by default when using `}<inlineCode parentName="p">{`setGlobalTheme()`}</inlineCode>{`.`}</p>
    <h3 {...{
      "id": "set-up-themes-for-apps-connect-forge-marketplace"
    }}>{`Set up themes for apps (Connect, Forge, Marketplace)`}</h3>
    <p>{`For Connect and Forge apps, see the following dedicated resources:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://developer.atlassian.com/cloud/jira/platform/connect-theming/"
        }}>{`Theming Connect apps`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://developer.atlassian.com/platform/forge/design-tokens-and-theming/"
        }}>{`Theming Forge apps`}</a></li>
    </ul>
    <h3 {...{
      "id": "server-side-rendering"
    }}>{`Server-side rendering`}</h3>
    <p>{`If your application is using SSR (server-side rendering) ensure themes appear at the right time
using our
`}<a parentName="p" {...{
        "href": "https://atlassian.design/components/tokens/code#server-side-rendering-ssr-utilities"
      }}>{`SSR theming utilities`}</a>{`.`}</p>
    <h2 {...{
      "id": "use-tokens"
    }}>{`Use tokens`}</h2>
    <p>{`You can access individual tokens using the CSS custom properties mounted to the page, however, it's
best to use the `}<inlineCode parentName="p">{`token()`}</inlineCode>{` method. This ensures you have proper prefixes, type checking, and linting
so you'll know if a token ever changes, is deleted, or is used incorrectly.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`token()`}</inlineCode>{` function takes a dot-separated token name and returns a valid CSS custom property for
the corresponding token. This method will warn you if an unknown token is provided.`}</p>
    <CodeBlock language="jsx" text={codeUseTokens} shouldWrapLongLines mdxType="CodeBlock" />
    <br></br>
    <p>{`If you're migrating a lot of values to tokens,
`}<a parentName="p" {...{
        "href": "https://atlassian.design/tokens/migrate-to-tokens#use-our-codemod-for-migration-assistance"
      }}>{`use our codemod`}</a>{`.`}</p>
    <h3 {...{
      "id": "migration-to-tokens"
    }}>{`Migration to tokens`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`token()`}</inlineCode>{` function accepts two arguments: the first is the token, and the second is an optional
fallback value (the value that is rendered when the theme is not set up). During the theme rollout
(i.e., migration from raw values to tokens), it is recommended to use the function without
specifying fallback value. In such cases, this value will be automatically supplied by the
build-time Babel plugin, ensuring that properties have valid values even if the theme is not yet
enabled.`}</p>
    <p>{`At times, token values may not perfectly align with the raw values used in the codebase. To maintain
consistent user experience, it is recommended to use the values provided by tokens whenever
possible. However, if no appropriate token exists for a specific value that must be retained, it is
preferable to leave the value unchanged (without converting it to a token) rather than adding it as
a fallback.`}</p>
    <p>{`Fallback values were mostly used historically for large app migrations and are not recommended
anymore.`}</p>
    <p>{`Design tokens are the new way to apply visual foundations in Atlassian app experiences. We’re
rolling out tokens to standardize colors, elevations, spacing, and other styles in Atlassian apps.`}</p>
    <h4 {...{
      "id": "babel-plugin"
    }}>{`Babel plugin`}</h4>
    <p>{`Our build-time Babel plugin is required to optimize the runtime performance of tokens by replacing
any calls to the `}<inlineCode parentName="p">{`token()`}</inlineCode>{` function in `}<inlineCode parentName="p">{`@atlaskit/tokens`}</inlineCode>{` with the corresponding CSS value the
function would return.`}</p>
    <p>{`If no fallback is provided, the plugin automatically retrieves the token value from the default
Atlassian light theme and sets it as the fallback. This behavior can be disabled by setting the
plugin second option `}<inlineCode parentName="p">{`shouldUseAutoFallback`}</inlineCode>{` to `}<inlineCode parentName="p">{`false`}</inlineCode>{`.`}</p>
    <CodeBlock language="diff" text={codeBabelPlugin} shouldWrapLongLines mdxType="CodeBlock" />
    <br></br>
    <p>{`By default, the plugin is configured to ignore explicit fallback values optionally provided in the
second argument of the token() function. This behavior can be overridden by explicitly setting the
plugin configuration option `}<inlineCode parentName="p">{`shouldForceAutoFallback`}</inlineCode>{` to `}<inlineCode parentName="p">{`false`}</inlineCode>{`.`}</p>
    <p><strong parentName="p">{`Usage`}</strong></p>
    <p>{`Add the plugin to your babel configuration:`}</p>
    <CodeBlock language="json" text={codeBabelPluginUsage} shouldWrapLongLines mdxType="CodeBlock" />
    <br></br>
    <p>{`If your codebase uses `}<a parentName="p" {...{
        "href": "https://compiledcssinjs.com/docs/installation#babel"
      }}>{`Compiled`}</a>{`, then add the
plugin to its configuration as well:`}</p>
    <CodeBlock language="json" text={codeBabelPluginCompiled} shouldWrapLongLines mdxType="CodeBlock" />
    <br></br>
    <h3 {...{
      "id": "removing-existing-token-fallbacks"
    }}>{`Removing existing token fallbacks`}</h3>
    <p>{`If fallback values were used during the migration to tokens, it is essential to remove them from the
codebase once migration is complete and themes are enabled. This helps to improve performance by
reducing the critical CSS size and simplifies development efforts.`}</p>
    <h4 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h4>
    <p>{`Ensure the following before proceeding:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Babel plugin enabled`}</strong>{`: The `}<inlineCode parentName="li">{`@atlaskit/tokens/babel-plugin`}</inlineCode>{` should be active with
`}<inlineCode parentName="li">{`shouldUseAutoFallback`}</inlineCode>{` set to `}<inlineCode parentName="li">{`true`}</inlineCode>{`. This ensures fallback values are automatically handled
during build time.`}</li>
      <li parentName="ul"><strong parentName="li">{`Themes enabled`}</strong>{`: Verify that ADS themes are applied in your app. Fallbacks can only be safely
removed for token categories with active themes (e.g color, space, or typography tokens).`}</li>
    </ul>
    <h4 {...{
      "id": "steps-for-fallback-removal"
    }}>{`Steps for fallback removal`}</h4>
    <ol>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Implicit Fallback Removal`}</strong>{`:`}</p>
        <ul parentName="li">
          <li parentName="ul">{`Update your Babel plugin configuration to include `}<inlineCode parentName="li">{`shouldForceAutoFallback: true`}</inlineCode>{`. This setting
forces the plugin to ignore any explicit fallbacks in the code, using default theme values
instead.`}</li>
          <li parentName="ul">{`Define `}<inlineCode parentName="li">{`forceAutoFallbackExemptions`}</inlineCode>{` for token categories that do not currently have an active
theme. This ensures the preservation of existing token fallbacks rendered in the app,
preventing unintended UI changes.`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li"><strong parentName="p">{`Explicit fallback removal`}</strong>{`:`}</p>
        <ul parentName="li">
          <li parentName="ul">{`Install the codemod: `}<a parentName="li" {...{
              "href": "https://www.npmjs.com/package/@atlaskit/codemod-cli"
            }}>{`https://www.npmjs.com/package/@atlaskit/codemod-cli`}</a></li>
          <li parentName="ul">{`Run the codemod to remove fallback values from your codebase:`}
            <CodeBlock language="shell" text={codeFallbackRemovalCodemod} shouldWrapLongLines mdxType="CodeBlock" />
          </li>
          <li parentName="ul">{`Use `}<inlineCode parentName="li">{`--skipTokens`}</inlineCode>{` to align with `}<inlineCode parentName="li">{`forceAutoFallbackExemptions`}</inlineCode>{` for any themes that are not yet
enabled (note that currently radius tokens are automatically exempted).`}</li>
          <li parentName="ul">{`Use `}<inlineCode parentName="li">{`--forceUpdate`}</inlineCode>{` to remove fallbacks regardless of their values. Without the option it would
preserve fallbacks that differ from the token value. Omitting this option is only valuable when
you don’t have `}<inlineCode parentName="li">{`shouldForceAutoFallback`}</inlineCode>{` enabled or are unsure if themes are enabled in the app
and therefore would like to remove fallbacks gradually starting with the ones that won’t have
UI impact if removed.`}</li>
          <li parentName="ul">{`Use `}<inlineCode parentName="li">{`--addEslintComments`}</inlineCode>{` to automatically add eslint suppressions for the fallbacks that
shouldn’t be removed, such as the ones configured in `}<inlineCode parentName="li">{`forceAutoFallbackExemptions`}</inlineCode>{`.`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Use ESLint to ensure fallbacks are not used in the future. This can be done by enabling the
`}<inlineCode parentName="p">{`@atlaskit/design-system/no-unsafe-design-token-usage`}</inlineCode>{`
`}<a parentName="p" {...{
            "href": "https://www.npmjs.com/package/@atlaskit/eslint-plugin-design-system"
          }}>{`ESLint rule`}</a>{` with
`}<inlineCode parentName="p">{`fallbackUsage: 'none'`}</inlineCode>{` to prevent new fallbacks from being added.`}</p>
      </li>
    </ol>
    <h2 {...{
      "id": "stay-up-to-date"
    }}>{`Stay up to date`}</h2>
    <p>{`As our color system is applied to new apps, changes to our tokens will be released regularly. To
stay up to date, install our linting support tools:`}</p>
    <ul>
      <li parentName="ul">{`For CSS in JS applications, use
`}<a parentName="li" {...{
          "href": "https://atlassian.design/components/eslint-plugin-design-system/usage"
        }}>{`ESLint`}</a>{`.`}</li>
      <li parentName="ul">{`For CSS (vanilla), Less, Sass, use
`}<a parentName="li" {...{
          "href": "https://atlassian.design/components/stylelint-design-system/usage"
        }}>{`Stylelint`}</a>{`.`}</li>
    </ul>
    <p>{`These will warn you if you’re using deprecated tokens, and will assist in updating your app to the
latest tokens.`}</p>
    <CodeBlock language="js" text={codeLintRules} shouldWrapLongLines mdxType="CodeBlock" />
    <h2 {...{
      "id": "whats-next"
    }}>{`What's next`}</h2>
    <ul>
      <li parentName="ul">{`Search the list of `}<a parentName="li" {...{
          "href": "https://atlassian.design/components/tokens/all-tokens"
        }}>{`all design tokens`}</a>{` for
full descriptions and token values.`}</li>
      <li parentName="ul">{`See design token `}<a parentName="li" {...{
          "href": "https://atlassian.design/components/tokens/examples"
        }}>{`code examples`}</a>{`.`}</li>
      <li parentName="ul">{`Browse our `}<a parentName="li" {...{
          "href": "https://atlassian.design/components/tokens/code"
        }}>{`token API reference and changelog`}</a>{`.`}</li>
      <li parentName="ul">{`Use the `}<a parentName="li" {...{
          "href": "https://atlassian.design/component/image"
        }}>{`image component`}</a>{` to theme images.`}</li>
    </ul>
    <h2 {...{
      "id": "get-help"
    }}>{`Get help`}</h2>
    <ul>
      <li parentName="ul">{`Atlassians can get help with tokens in `}<a parentName="li" {...{
          "href": "https://go.atlassian.com/help-design-system"
        }}>{`Slack`}</a>{`.`}</li>
      <li parentName="ul">{`Partners and other developers who need help with tokens can post in the
`}<a parentName="li" {...{
          "href": "https://community.developer.atlassian.com/"
        }}>{`developer community`}</a>{`.`}</li>
      <li parentName="ul">{`For general help with the Atlassian Design system,
`}<a parentName="li" {...{
          "href": "https://atlassian.design/contact-us"
        }}>{`contact us`}</a>{`.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      